import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { DeliveryChips } from '../../../../../components/deliveries/DeliveryChips';
import { ChartTooltip } from './ChartTooltip';
import { CHART_MIN_HEIGHT } from '../../../../../features/dashboard/dashboardConstants';
import { buildDonutData, formatNumber } from '../../../../../features/dashboard/dashboardUtils';

/**
 * The donut and its chips.
 *
 * The body only: the card, the title and the tab are `DashboardCharts`, shared
 * with the daily volume chart.
 *
 * The chips are the deliveries table's own component, so a status is the same
 * colour and the same name on both screens — the mockup carried six invented
 * hexes and six free-form labels.
 *
 * Clicking one leaves for the deliveries table with that status applied rather
 * than filtering this card: a dashboard filtered to a single status is one
 * slice at 100% and four KPIs that no longer mean anything, which is why the
 * legacy sends `delivery_status: null` on purpose.
 *
 * @param {object} props
 * @param {Array<{value: string, count: number}>} props.statusCount
 * @param {number} props.total
 * @param {(status: string) => void} props.onStatusClick
 * @param {boolean} props.isLoading
 */
export const StatusDistributionChart = ({
  statusCount,
  total,
  onStatusClick,
  isLoading,
}) => {
  const data = useMemo(() => buildDonutData(statusCount), [statusCount]);
  const hasData = data.length > 0;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="mb-6 flex-shrink-0">
        <DeliveryChips
          total={formatNumber(total)}
          statusCount={statusCount}
          currentStatus="all"
          onStatusChange={onStatusClick}
        />
      </div>

      <div className={`flex-1 min-h-0 flex items-center justify-center ${CHART_MIN_HEIGHT}`}>
        {isLoading && !hasData && (
          <div className="w-[240px] h-[240px] rounded-full bg-ninja-gray dark:bg-[#2d2d2d] animate-pulse" />
        )}

        {!isLoading && !hasData && (
          <p className="text-sm text-text-muted">No hay envíos en el periodo seleccionado</p>
        )}

        {hasData && (
          <div className="w-full h-full flex items-center gap-6">
            <div className="flex-1 h-full min-w-0 relative">
              {/* The hole in the middle is where the eye lands first, and it
                  used to be empty. */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-3xl font-extrabold text-ninja-dark dark:text-white leading-none">
                  {formatNumber(total)}
                </span>
                <span className="text-xs text-text-secondary mt-1">envíos</span>
              </div>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    innerRadius={95}
                    outerRadius={140}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                  >
                    {data.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  {/* A real component, not `contentStyle`: that one had no
                      background colour, so it inherited white and drew
                      white-on-white in dark mode. */}
                  <Tooltip content={(props) => <ChartTooltip {...props} />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Its own column rather than absolutely positioned over the chart:
                with nineteen possible statuses the mockup's overlay would sit
                on top of the donut as soon as more than six show up. */}
            <div className="flex flex-col gap-2.5 pr-2 max-h-full overflow-y-auto">
              {data.map((item) => (
                <div
                  key={item.name}
                  className="flex items-center gap-2 text-sm text-text-secondary font-medium whitespace-nowrap"
                >
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                  {item.name}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
